import React from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, ArrowRight, Quote } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { business, testimonials } from "../data/content";

const AVATAR_COLORS = [
  "bg-[hsl(120,25%,45%)]", // green
  "bg-[hsl(280,45%,55%)]", // purple
  "bg-[hsl(20,55%,55%)]",  // terracotta
  "bg-[hsl(340,45%,55%)]", // pink
  "bg-[hsl(200,45%,50%)]", // blue
];

function initials(name) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function Reviews() {
  useSeo({
    title: `Reviews | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Verified 5-star Google reviews for Soft Petal Electrolysis in ${business.city}, ${business.state}. Real stories, real results, renewed confidence.`,
    path: "/reviews",
  });

  const avg = (testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length).toFixed(1);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg,
      reviewCount: testimonials.length,
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
      reviewBody: t.quote,
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="watercolor-blush py-16 lg:py-20 relative overflow-hidden">
        <FloatingPetals count={6} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Reviews"
            title="Real stories,"
            scriptWord="renewed confidence"
            subtitle="Verified 5-star Google reviews from our Soft Petal Pals."
          />
          <div className="mt-8 inline-flex items-center gap-3 bg-warm-white/80 backdrop-blur px-5 py-3 rounded-full border border-pale-rose/60 shadow-soft">
            <div className="flex text-petal">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={16} className="fill-petal" strokeWidth={1} />
              ))}
            </div>
            <span className="font-review text-sm font-semibold text-charcoal">
              {avg} average
            </span>
            <span className="font-review text-xs text-charcoal-soft">
              · {testimonials.length}+ Google reviews
            </span>
          </div>
        </div>
      </section>

      <section className="pt-10 lg:pt-14 pb-14">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-2 gap-7">
            {testimonials.map((t, i) => (
              <Reveal
                key={i}
                delay={i * 110}
                className="relative bg-warm-white rounded-3xl p-8 sm:p-9 border border-pale-rose/50 shadow-soft lift-on-hover"
                data-testid={`review-card-${i}`}
              >
                <Quote
                  className="absolute top-6 right-6 text-petal/25"
                  size={44}
                  strokeWidth={1}
                />
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-review font-semibold ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                    aria-hidden="true"
                  >
                    {initials(t.name)}
                  </div>
                  <div>
                    <div className="font-review font-semibold text-base leading-tight text-charcoal">
                      {t.name}
                    </div>
                    <div className="font-review text-xs text-charcoal-soft mt-0.5 flex items-center gap-2">
                      <span>{t.date}</span>
                      <span aria-hidden>·</span>
                      <span className="inline-flex items-center gap-1">
                        <GoogleG /> {t.source}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-1 text-petal">
                  {Array.from({ length: t.rating }).map((_, r) => (
                    <Star
                      key={r}
                      size={18}
                      className="fill-petal sparkle"
                      strokeWidth={1}
                      style={{ animationDelay: `${r * 180}ms` }}
                    />
                  ))}
                </div>
                <p className="mt-5 font-review text-[0.95rem] text-charcoal leading-relaxed">
                  {t.quote}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16 bg-blush/50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Loved your visit?" scriptWord="Share it with the world." />
          <p className="mt-6 font-body text-charcoal-soft max-w-xl mx-auto leading-relaxed">
            Your kind words help other Soft Petal Pals discover a warm, judgment-free space of their own.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href={business.social.google}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-petal"
              data-testid="google-review-btn"
            >
              Leave a Google Review
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Ready to write your" scriptWord="own story?" />
          <BookLink  className="btn-petal mt-8 inline-flex" data-testid="reviews-cta-book">
            Book Your Appointment
            <ArrowRight size={14} />
          </BookLink>
        </div>
      </section>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="12" height="12" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
